/**
 * "Add As negative" dropdown for the Search Terms tab. Surfaces only
 * when ≥1 search term is selected.
 *
 * Two-stage popup:
 *   1. Three options:
 *        - To That Ad Group
 *        - To That Campaign
 *        - To Selected Campaigns
 *   2. Picking the third option swaps the popup body for a search +
 *      checkbox list of the user's campaigns, with a Confirm button
 *      that fires onAddToSelected(campaignIds).
 *
 * Backend wiring lands in v1.1 — for now the three handlers are
 * stubs the parent can replace.
 */

import { useEffect, useRef, useState } from "react";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";

export type CampaignOption = { id: number; name: string };

export function AddAsNegativeMenu({
  selectedCount,
  hasAdGroupContext,
  campaignOptions,
  onAddToAdGroup,
  onAddToCampaign,
  onAddToSelectedCampaigns,
}: {
  selectedCount: number;
  /** When false, "To That Ad Group" is disabled (search-term row may
   *  span multiple ad groups, e.g. when nothing is filtered). */
  hasAdGroupContext: boolean;
  campaignOptions: CampaignOption[];
  onAddToAdGroup: () => void;
  onAddToCampaign: () => void;
  onAddToSelectedCampaigns: (campaignIds: number[]) => void;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState<"menu" | "campaigns">("menu");
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<number>>(new Set());

  useLayer(open, () => setOpen(false));
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, [open]);

  // Reset secondary stage whenever the popup closes — re-opening
  // should always start at the 3-option menu, not mid-flow.
  useEffect(() => {
    if (!open) {
      setStage("menu");
      setSearch("");
      setPicked(new Set());
    }
  }, [open]);

  const togglePick = (id: number) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredCampaigns = campaignOptions.filter((c) =>
    c.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  if (selectedCount === 0) return null;

  return (
    <div
      ref={wrapperRef}
      style={{ position: "relative", display: "inline-block" }}
    >
      <button
        type="button"
        className="btn sm"
        onClick={() => setOpen((v) => !v)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        title={`Add ${selectedCount} selected term${selectedCount === 1 ? "" : "s"} as negative`}
      >
        <span>Add As negative</span>
        {I.chevronDown}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            minWidth: 280,
            background: "var(--c-surface, white)",
            border: "1px solid var(--c-border, #ddd)",
            borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,.12)",
            zIndex: 50,
            padding: 4,
          }}
        >
          {stage === "menu" && (
            <>
              <DropdownItem
                label="To That Ad Group"
                disabled={!hasAdGroupContext}
                title={
                  hasAdGroupContext
                    ? undefined
                    : "Select a single ad group on the Ad Groups tab to enable"
                }
                onClick={() => {
                  onAddToAdGroup();
                  setOpen(false);
                }}
              />
              <DropdownItem
                label="To That Campaign"
                onClick={() => {
                  onAddToCampaign();
                  setOpen(false);
                }}
              />
              <DropdownItem
                label="To Selected Campaigns"
                onClick={() => setStage("campaigns")}
                trailingChevron
              />
            </>
          )}

          {stage === "campaigns" && (
            <div style={{ width: 320 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 6px 4px",
                }}
              >
                <button
                  type="button"
                  className="btn icon sm"
                  onClick={() => setStage("menu")}
                  title="Back"
                  aria-label="Back"
                >
                  ‹
                </button>
                <span style={{ fontSize: 12.5, fontWeight: 600 }}>
                  Pick campaigns
                </span>
              </div>
              <div className="input-group" style={{ margin: "4px 6px 6px" }}>
                {I.search}
                <input
                  className="input"
                  placeholder="Search campaigns"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  autoFocus
                />
              </div>
              <div
                style={{
                  maxHeight: 240,
                  overflowY: "auto",
                  borderTop: "1px solid var(--c-divider, #eee)",
                  borderBottom: "1px solid var(--c-divider, #eee)",
                }}
              >
                {filteredCampaigns.length === 0 && (
                  <div
                    className="muted"
                    style={{ padding: 14, fontSize: 12, textAlign: "center" }}
                  >
                    No campaigns match.
                  </div>
                )}
                {filteredCampaigns.map((c) => (
                  <label
                    key={c.id}
                    className="check"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      fontSize: 13,
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={picked.has(c.id)}
                      onChange={() => togglePick(c.id)}
                    />
                    <span style={{ flex: 1 }}>{c.name}</span>
                  </label>
                ))}
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "6px 8px",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: "var(--c-text-3, #777)",
                  }}
                >
                  {picked.size} selected
                </span>
                <button
                  type="button"
                  className="btn primary sm"
                  disabled={picked.size === 0}
                  onClick={() => {
                    onAddToSelectedCampaigns([...picked]);
                    setOpen(false);
                  }}
                >
                  Confirm
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DropdownItem({
  label,
  onClick,
  disabled,
  title,
  trailingChevron,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  title?: string;
  trailingChevron?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        if (!disabled) onClick();
      }}
      disabled={disabled}
      title={title}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        width: "100%",
        padding: "6px 10px",
        background: "transparent",
        border: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        fontSize: 13,
        color: disabled ? "var(--c-text-3, #999)" : "var(--c-text, #222)",
        borderRadius: 6,
      }}
      onMouseEnter={(e) => {
        if (!disabled)
          (e.currentTarget.style.background =
            "var(--c-surface-alt, #f5f5f5)");
      }}
      onMouseLeave={(e) => (e.currentTarget.style.background = "")}
    >
      <span>{label}</span>
      {trailingChevron && (
        <span style={{ color: "var(--c-text-4, #aaa)" }}>›</span>
      )}
    </button>
  );
}
